import React, { Component } from 'react';
import './AddressThumbnail.css';
import ApiHandler from 'src/api/ApiHandler';

interface AddressThumbnailProps {
    locationId: string;
    locationString: string;
    apiHandler: ApiHandler;
}

interface AddressThumbnailState {
    imageSrc: string | null;
    isLoading: boolean;
    hasError: boolean;
}

class AddressThumbnail extends Component<AddressThumbnailProps, AddressThumbnailState> {
    constructor(props: AddressThumbnailProps) {
        super(props);
        this.state = {
            imageSrc: null,
            isLoading: false,
            hasError: false,
        };
    }

    async componentDidMount() {
        await this.fetchThumbnail();
    }

    async componentDidUpdate(prevProps: AddressThumbnailProps) {
        if (
            prevProps.locationId !== this.props.locationId ||
            prevProps.locationString !== this.props.locationString
        ) {
            await this.fetchThumbnail();
        }
    }

    async fetchThumbnail() {
        const { locationId, locationString, apiHandler } = this.props;
        if (!locationId || !locationString || !apiHandler) {
            return;
        }

        this.setState({ isLoading: true, hasError: false });

        const maxRetries = 2;
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                const response = await apiHandler.getAddressThumbnail(locationId, locationString);

                if (response?.raw_image && response.valid_image === true) {
                    const imageSrc = `data:image/jpeg;base64,${response.raw_image}`;
                    this.setState({
                        imageSrc,
                        isLoading: false,
                        hasError: false,
                    });
                    return;
                } else {
                    this.setState({
                        imageSrc: null,
                        isLoading: false,
                        hasError: response?.raw_image && response.valid_image === false ? false : true,
                    });
                    return;
                }
            } catch (error) {
                if (attempt < maxRetries) {
                    await new Promise(r => setTimeout(r, 500 * Math.pow(2, attempt)));
                    continue;
                }
                console.error('Failed to fetch address thumbnail:', error);
                this.setState({
                    imageSrc: null,
                    isLoading: false,
                    hasError: true,
                });
            }
        }
    }

    render() {
        const { imageSrc, isLoading, hasError } = this.state;
        const { locationString } = this.props;

        const width = ApiHandler.THUMBNAIL_WIDTH;
        const height = ApiHandler.THUMBNAIL_HEIGHT;

        return (
            <div
                className="address-thumbnail"
                style={{ width, minWidth: width, height, minHeight: height }}
                aria-hidden
            >
                {isLoading && (
                    <div className="address-thumbnail-placeholder address-thumbnail-loading">
                        <span>…</span>
                    </div>
                )}
                {!isLoading && imageSrc && (
                    <img
                        src={imageSrc}
                        alt=""
                        className="address-thumbnail-image"
                        width={width}
                        height={height}
                    />
                )}
                {!isLoading && !imageSrc && hasError && (
                    <div className="address-thumbnail-placeholder address-thumbnail-unavailable">
                        <span>—</span>
                    </div>
                )}
                {!isLoading && !imageSrc && !hasError && (
                    <div className="address-thumbnail-placeholder address-thumbnail-unavailable">
                        <span>—</span>
                    </div>
                )}
            </div>
        );
    }
}

export default AddressThumbnail;
